import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { Undo2, X, CheckCircle2, AlertCircle, Info } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'info';
export type Toast = { id: number; message: string; type?: ToastType; undo?: () => void };

const ToastCtx = createContext<{ 
  push: (message: string, typeOrUndo?: ToastType | (() => void), undo?: () => void) => void 
} | null>(null);

let counter = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const remove = useCallback((id: number) => setToasts((t) => t.filter((x) => x.id !== id)), []);

  const push = useCallback(
    (message: string, typeOrUndo?: ToastType | (() => void), undo?: () => void) => {
      const id = ++counter;
      let toastType: ToastType = 'info';
      let undoFn: (() => void) | undefined = undefined;

      if (typeof typeOrUndo === 'function') {
        undoFn = typeOrUndo;
      } else if (typeof typeOrUndo === 'string') {
        toastType = typeOrUndo;
        undoFn = undo;
      } else {
        undoFn = undo;
      }

      // Auto-detect error or success if not explicitly specified
      if (typeof typeOrUndo !== 'string') {
        const lower = message.toLowerCase();
        if (
          lower.includes('unauthor') || 
          lower.includes('invalid') || 
          lower.includes('fail') || 
          lower.includes('error') || 
          lower.includes('denied') ||
          lower.includes('forbidden')
        ) {
          toastType = 'error';
        } else if (
          lower.includes('success') || 
          lower.includes('created') || 
          lower.includes('provisioned') ||
          lower.includes('updated') ||
          lower.includes('saved')
        ) {
          toastType = 'success';
        }
      }

      setToasts((t) => [...t, { id, message, type: toastType, undo: undoFn }]);
      setTimeout(() => remove(id), 6000);
    },
    [remove],
  );

  return (
    <ToastCtx.Provider value={{ push }}>
      {children}
      <div className="pointer-events-none fixed bottom-4 right-4 z-[120] flex flex-col gap-2.5">
        {toasts.map((t) => {
          const isErr = t.type === 'error';
          const isSucc = t.type === 'success';

          return (
            <div
              key={t.id}
              className={`animate-settle pointer-events-auto flex items-center gap-3 rounded-xl border px-4 py-3 text-sm shadow-xl backdrop-blur-md transition-all ${
                isErr 
                  ? 'border-red-500/40 bg-card/95 text-foreground shadow-red-500/10' 
                  : isSucc
                  ? 'border-emerald-500/30 bg-card/95 text-foreground shadow-emerald-500/10'
                  : 'border-border bg-card/95 text-foreground'
              }`}
            >
              {isErr ? (
                <AlertCircle className="h-4 w-4 flex-shrink-0 text-red-500" />
              ) : isSucc ? (
                <CheckCircle2 className="h-4 w-4 flex-shrink-0 text-emerald-500" />
              ) : (
                <Info className="h-4 w-4 flex-shrink-0 text-primary" />
              )}
              <span className="max-w-xs font-medium text-xs leading-relaxed">{t.message}</span>
              {t.undo && (
                <button
                  onClick={() => { t.undo!(); remove(t.id); }}
                  className="ml-1 inline-flex items-center gap-1 rounded-md bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary transition hover:bg-primary/20"
                >
                  <Undo2 className="h-3.5 w-3.5" /> Undo
                </button>
              )}
              <button 
                onClick={() => remove(t.id)} 
                className="text-muted-foreground hover:text-foreground transition-colors ml-1 p-0.5 rounded"
                title="Dismiss"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          );
        })}
      </div>
    </ToastCtx.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastCtx);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}

